import { typedApi } from "@/api/typed-client";
import { mapApiError } from "@/lib/errors";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type { DatasetListItem } from "./queries";

export interface UploadStartParams {
  name: string;
  format: "csv" | "parquet";
  size_bytes: number;
  project_id: string;
}

interface UploadStartResponse {
  upload_url: string;
  upload_token: string;
  dataset_id_pending: string;
  expires_in: number;
}

export function useStartUpload() {
  return useMutation({
    mutationFn: async (body: UploadStartParams) => {
      const { data, error } = await typedApi.POST("/v1/datasets/upload", {
        body,
      });
      if (error) throw new Error(JSON.stringify(error));
      // Justified: UploadStartOut schema matches UploadStartResponse but openapi-fetch infers a wider type
      return data as unknown as UploadStartResponse;
    },
    onError: (e) => toast.error(`Upload failed: ${(e as Error).message}`),
  });
}

export function useConfirmUpload() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (upload_token: string) => {
      const { data, error } = await typedApi.POST("/v1/datasets/upload/confirm", {
        body: { upload_token },
      });
      if (error) throw new Error(JSON.stringify(error));
      // Justified: confirm response is DatasetSummary in openapi but UI expects DatasetListItem subset
      return data as unknown as DatasetListItem;
    },
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ["datasets"] });
      toast.success(`Dataset "${d.name}" uploaded — profiling started`);
    },
    onError: (e) => toast.error(`Confirm failed: ${(e as Error).message}`),
  });
}

export function useDeleteDataset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await typedApi.DELETE("/v1/datasets/{dataset_id}", {
        params: { path: { dataset_id: id } },
      });
      if (error) throw new Error(JSON.stringify(error));
      return null;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["datasets"] });
      toast.success("Dataset deleted");
    },
    onError: (e) => toast.error(`Delete failed: ${(e as Error).message}`),
  });
}

export function useDownloadDataset() {
  return useMutation({
    mutationFn: async (id: string) => {
      const resp = await fetch(`/v1/datasets/${id}/download-url`);
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        const code = body?.error?.code ?? "unknown";
        const mapped = mapApiError(resp.status, code, body);
        throw new Error(mapped.message);
      }
      const { download_url } = (await resp.json()) as { download_url: string };
      window.open(download_url, "_blank");
    },
    onError: (e) => toast.error((e as Error).message),
  });
}
